/**
 * Date Formatting Utilities
 * Functions for formatting dates in various formats
 */

import dayjs, { type Dayjs } from 'dayjs';

// Type for dayjs input - matches what dayjs() constructor accepts
type DayjsInput = string | number | Date | Dayjs | null | undefined;

/**
 * Format date to ISO string (YYYY-MM-DDTHH:mm:ss)
 * Replaces duplicate formatDateToISO functions across the codebase
 */
export function formatDateToISO(date: DayjsInput): string {
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * Format date to ISO date string (YYYY-MM-DD)
 */
export function formatDateToISODate(date: DayjsInput): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Format date for API requests (UTC format: YYYY-MM-DDTHH:mm:ss)
 * Used for API date formatting
 */
export function formatForAPI(date: DayjsInput): string {
  return dayjs.utc(date).format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * Format date for API requests with date only (UTC format: YYYY-MM-DD)
 */
export function formatForAPIDate(date: DayjsInput): string {
  return dayjs.utc(date).format('YYYY-MM-DD');
}

/**
 * Format date to locale string with consistent formatting
 * Replaces inconsistent toLocaleString() calls
 */
export function formatToLocaleString(date: DayjsInput, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    hour12: false,
    ...options,
  };
  return `${dayjs(date).toDate().toLocaleString('en-GB', defaultOptions)} UTC`;
}

/**
 * Format date to locale date string (date only)
 * Replaces toLocaleDateString() calls
 */
export function formatToLocaleDate(date: DayjsInput, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
    ...options,
  };
  return dayjs(date).toDate().toLocaleDateString(undefined, defaultOptions);
}

/**
 * Format date to locale time string (time only)
 * Replaces toLocaleTimeString() calls
 */
export function formatToLocaleTime(date: DayjsInput, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    hour12: false,
    ...options,
  };
  return `${dayjs(date).toDate().toLocaleTimeString('en-GB', defaultOptions)} UTC`;
}

/**
 * Format date to DateString format (e.g., "Mon Jan 01 2024")
 */
export function formatToDateString(date: DayjsInput): string {
  return dayjs(date).toDate().toDateString();
}
